"use client";

import Image from "next/image";
import React, { useState } from "react";
import { usePathname } from "next/navigation";
import GlobalForm from "@/components/forms/GlobalForm";
import {
  FinanceAndAccountingSubcategory,
  FormHeading,
  SuccessMessageType,
  VerticalCategory,
} from "@/interface/types";

interface JobMncsCpaProps {
  heading: React.ReactNode;
  pageName: string;
}

/* ---------------- Types ---------------- */
type CardProps = {
  title: string;
  amount: string;

  // Desktop subtitle (single line)
  subtitleDesktop: string;
  // Mobile subtitle split so we can force a line break
  subtitleMobileTop: string; // e.g., "Work at Big 4 &"
  subtitleMobileBottom: string; // e.g., "220+ MNCs"

  // Desktop logo strip
  logoPanelDeskSrc: string;
  logoPanelDeskAlt: string;
  logoPanelDeskW: number;
  logoPanelDeskH: number;

  // Mobile logo strip
  logoPanelMobSrc: string;
  logoPanelMobAlt: string;
  logoPanelMobW: number;
  logoPanelMobH: number;
};

/* ---------------- Card ---------------- */
function SalaryCard({
  title,
  amount,
  subtitleDesktop,
  subtitleMobileTop,
  subtitleMobileBottom,
  logoPanelDeskSrc,
  logoPanelDeskAlt,
  logoPanelDeskW,
  logoPanelDeskH,
  logoPanelMobSrc,
  logoPanelMobAlt,
  logoPanelMobW,
  logoPanelMobH,
}: CardProps) {
  return (
    <div className="w-full bg-white relative mx-auto">
      <div className="mx-auto">
        <div className="relative rounded-[12px] border-[1px] border-[rgba(248,248,248,0.3)] bg-[#3399FF] backdrop-blur-[8.537px]">
          {/* Header */}
          <div className="px-1 md:px-6 pt-2 md:pt-3 text-white">
            <p className="text-xs md:text-lg font-semibold leading-4">
              {title}
            </p>
            <p className="text-[26px] md:text-[32px] font-bold tracking-tight leading-8">
              {amount}
            </p>
          </div>

          {/* Body */}
          <div className="m-2 md:m-3 rounded-[12px] bg-[#F8F8F8] border border-[#EDEDF0]">
            <div className="px-4 md:px-6 py-2 md:py-3">
              {/* Subtitle: desktop single line, mobile forced break */}
              <p className="text-center text-black text-xs md:text-lg font-medium md:block hidden">
                {subtitleDesktop}
              </p>
              <p className="text-center md:text-black text-[rgba(0,15,52,0.6)] text-xs font-medium md:hidden block">
                {subtitleMobileTop} <br className="md:hidden" />
                {subtitleMobileBottom}
              </p>

              {/* Logo strip (desktop) */}
              <div className="mt-4 md:mt-6 justify-center hidden md:flex">
                <Image
                  src={logoPanelDeskSrc}
                  alt={logoPanelDeskAlt}
                  width={logoPanelDeskW}
                  height={logoPanelDeskH}
                  className="h-12 w-auto object-contain"
                />
              </div>

              {/* Logo strip (mobile) */}
              <div className="mt-3 justify-center flex md:hidden">
                <Image
                  src={logoPanelMobSrc}
                  alt={logoPanelMobAlt}
                  width={logoPanelMobW}
                  height={logoPanelMobH}
                  className="w-auto object-contain"
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ---------------- Page ---------------- */
const SalaryShowcase: React.FC<JobMncsCpaProps> = ({ heading, pageName }) => {
  const pathname = usePathname();
  const [isFormPopupOpen, setFormPopupOpen] = useState(false);

  // Hide button on specific URL
  const shouldHideButton =
    pathname === "/best-us-cpa-admission-online-course-fees-in-india";

  const openFormPopup = () => {
    sessionStorage.setItem("modal-open", "true");
    setFormPopupOpen(true);
  };
  const closeFormPopup = () => {
    sessionStorage.removeItem("modal-open");
    setFormPopupOpen(false);
  };

  const cardsData: CardProps[] = [
    {
      title: "Avg. Salary in India",
      amount: "₹20 lakhs+",
      subtitleDesktop: "Work at Big 4 & 220+ MNCs",
      subtitleMobileTop: "Work at Big 4 &",
      subtitleMobileBottom: "220+ MNCs",

      logoPanelDeskSrc:
        pageName === "CPA"
          ? "https://ik.imagekit.io/mileseducation/miles_website/accounting/cpa-visit-new/big4_20lakhs.webp"
          : "https://ik.imagekit.io/mileseducation/miles_website/accounting/cpa-visit-new/20lakhs_desktop_cma.webp",
      logoPanelDeskAlt: "Deloitte, PwC, KPMG, EY",
      logoPanelDeskW: 640,
      logoPanelDeskH: 120,

      logoPanelMobSrc:
        pageName === "CPA"
          ? "https://ik.imagekit.io/mileseducation/miles_website/accounting/cpa-visit-new/big4_20lakhs_mob.webp"
          : "https://ik.imagekit.io/mileseducation/miles_website/accounting/cpa-visit-new/20lakhs_mobile_cma.webp",
      logoPanelMobAlt: "Deloitte, PwC, KPMG, EY (mobile)",
      logoPanelMobW: 360,
      logoPanelMobH: 80,
    },
    {
      title: "Avg. Salary in USA",
      amount: "₹50 lakhs+",

      subtitleDesktop:
        pageName === "CPA"
          ? "Work at 350+ CPA firms"
          : "Work at 350+ CMA firms",
      subtitleMobileTop: "Work at 350+",
      subtitleMobileBottom: pageName === "CPA" ? "CPA firms" : "CMA firms",

      // Desktop logos
      logoPanelDeskSrc:
        "https://ik.imagekit.io/mileseducation/miles_website/accounting/cpa-visit-new/big4_50lakhs.webp",
      logoPanelDeskAlt: "RSM, BDO Alliance USA, Allinial Global, CPAmerica",
      logoPanelDeskW: 640,
      logoPanelDeskH: 120,

      // Mobile logos (use your mobile-optimized strip here + sizes)
      logoPanelMobSrc:
        "https://ik.imagekit.io/mileseducation/miles_website/accounting/cpa-visit-new/big4_50lakhs_mob.webp",
      logoPanelMobAlt:
        "RSM, BDO Alliance USA, Allinial Global, CPAmerica (mobile)",
      logoPanelMobW: 360,
      logoPanelMobH: 80,
    },
  ];

  return (
    <>
      {/* Heading */}
      <section
        className={`w-full bg-white  md:py-12 ${shouldHideButton ? "py-4" : "py-8"}`}
      >
        <div className="main_container mx-auto px-4 text-center">
          {heading}

          {/* Cards */}
          <div className="grid grid-cols-2 gap-2 md:gap-7 md:w-[88%] mx-auto md:mt-10 mt-7">
            {cardsData?.map((card, i) => (
              <SalaryCard key={i} {...card} />
            ))}
          </div>

          {/* CTA - Hidden on /best-us-cpa-admission-online-course-fees-in-india */}
          {!shouldHideButton && (
            <button
              className="md:mt-8 mt-6 block mx-auto bg-[#2A85FF] tracking-wide rounded-[12px_12px_12px_0px] text-white font-bold py-1 px-7 md:h-[32px] text-sm text-center"
              onClick={openFormPopup}
            >
              Register for Free Webinar
            </button>
          )}
        </div>
      </section>

      {/* Form popup */}
      {isFormPopupOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-80 flex justify-center items-center z-[999] lg:p-10 p-4">
          <div
            className="rounded-2xl shadow-lg relative h-auto mx-auto border border-white p-4 w-[400px]"
            style={{
              background:
                "linear-gradient(180deg, rgba(255, 255, 255, 0.30) 0%, rgba(51, 153, 255, 0.30) 100%)",
              boxShadow: "0px 0px 14px 0px rgba(4, 84, 175, 0.29)",
              backdropFilter: "blur(28px)",
            }}
          >
            <button
              onClick={closeFormPopup}
              className="mt-2 text-white px-3 text-lg py-1 rounded absolute right-[10px] top-[0px] md:top-[-6px] z-[9999]"
            >
              ✕
            </button>
            <Image
              src="https://ik.imagekit.io/mileseducation/miles_website/accounting/cpa-visit/starGroup.webp"
              alt="star group"
              width={34}
              height={38}
              className="absolute top-3 left-[-8px]"
              loading="lazy"
            />
            <GlobalForm
              course={pageName}
              courseId={pageName === "CPA" ? 1 : 2}
              formHeading={FormHeading.WORLD_FAVORITE_CMA_CPA_INSTRUCTOR}
              formType="page"
              isAStepperForm
              pageType="accounting page"
              subCategory={
                pageName === "CPA"
                  ? FinanceAndAccountingSubcategory.CPA
                  : FinanceAndAccountingSubcategory.CMA
              }
              submitButtonName="Register for Free Webinar"
              successMessage={SuccessMessageType.ACCOUNTING}
              verticalName={VerticalCategory.FINANCE_AND_ACCOUNTING}
              darkBackground
            />
          </div>
        </div>
      )}
    </>
  );
};

export default SalaryShowcase;
